import { FC, useState } from 'react';
import { Field, FieldProps } from 'formik';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';

const theme = createTheme({
  palette: {
    primary: {
      main: '#26A46F',
    },
  },
  typography: {
    fontFamily: 'Montserrat',
  },
});

interface UserDateInputProps {
  name: string;
  prompt: string;
  inputFormat?: string;
  allowPastDates?: boolean;
  dateLimitEarly?: string;
  dateLimitLate?: string;
  className?: string;
}

export const UserDateInput: FC<UserDateInputProps> = ({
  name,
  prompt,
  inputFormat,
  allowPastDates = false,
  dateLimitEarly,
  dateLimitLate,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`w-full ${className}`}>
      <div className="flex flex-col mb-2 text-sm font-bold text-text-dark">{prompt}</div>
      <div className="sm:flex sm:flex-row">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeProvider theme={theme}>
            <Field name={name}>
              {({ field, form }: FieldProps<Dayjs>) => {
                return (
                  <DatePicker
                    format={inputFormat ?? 'YYYY-MM-DD'}
                    disablePast={!allowPastDates}
                    value={
                      field.value ? dayjs(field.value) : null /* convert undefined to null to display empty selection */
                    }
                    open={open}
                    onChange={(newDate) => {
                      const date = newDate ?? undefined; // convert nulls back to undefined
                      if (dateLimitEarly && dayjs(date).diff(dayjs(dateLimitEarly)) < 0) {
                        form.setFieldValue(name, dateLimitEarly);
                      } else if (dateLimitLate && dayjs(dateLimitLate).diff(dayjs(date)) < 0) {
                        form.setFieldValue(name, dateLimitLate);
                      } else {
                        form.setFieldValue(name, date);
                      }
                    }}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    closeOnSelect={true}
                    slotProps={{
                      toolbar: {
                        hidden: true,
                      },
                      actionBar: {
                        actions: ['today', 'clear'],
                      },
                      field: {
                        readOnly: true,
                        sx: {
                          height: 20,
                        },
                      },
                      textField: {
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            height: 50,
                            borderRadius: 2,
                            '& fieldset': {
                              borderColor: '#B4B4C5 !important',
                              opacity: '20%',
                            },
                            '&:hover fieldset': {
                              borderColor: '#26A46F !important',
                              borderWidth: 0,
                              borderBottom: 2,
                              opacity: '100%',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#26A46F !important',
                              borderWidth: 0,
                              borderBottom: 2,
                              opacity: '100%',
                            },
                          },
                        },
                        className: 'rounded-lg bg-input-gray hover:bg-sami-hover hover:border-sami-green',
                      },
                    }}
                  />
                );
              }}
            </Field>
          </ThemeProvider>
        </LocalizationProvider>
      </div>
    </div>
  );
};
